/**
 * Sélecteur de filtre réutilisable pour la barre de filtres globale : une
 * Combobox (Popover + Command/cmdk) avec recherche textuelle, utilisée pour
 * les listes potentiellement longues (Site, Personne).
 */
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function FilterSelect({ testId, label, value, onChange, options, searchPlaceholder, loading }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => options.find((opt) => opt.value === value), [options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="h-8 w-auto min-w-[150px] max-w-[220px] justify-between gap-1.5 border-slate-200 bg-white px-2.5 text-xs font-normal"
        >
          <span className="flex min-w-0 items-center gap-1">
            <span className="text-slate-400">{label}:</span>
            <span className="truncate">{selected?.label ?? "..."}</span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput data-testid={`${testId}-search-input`} placeholder={searchPlaceholder ?? `Rechercher ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty data-testid={`${testId}-empty`}>
              {loading ? "Chargement..." : "Aucun résultat."}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.searchText ?? opt.label}
                  data-testid={`${testId}-option-${opt.value}`}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
